import React, { useState } from "react";
import { IconButton, Menu } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  MenuItemCompany,
  MenuItemDataLimit,
  MenuItemDocument,
  MenuItemFeature,
  MenuItemModule,
  MenuItemNotification,
  MenuItemPartner,
  MenuItemRole,
  MenuItemSubscription,
  MenuItemTenant,
  MenuItemUser,
  MenuItemLocation,
  MenuItemMeter,
} from "@/components";

type MenuForType =
  | "user"
  | "system"
  | "partner"
  | "tenant"
  | "role"
  | "module"
  | "subscription"
  | "feature"
  | "company"
  | "data-limit"
  | "notification"
  | "document"
  | "location"
  | "meter";

export type MenuClickHandler<
  TAction = string,
  TRow extends object = Record<string, unknown>,
> = (action: TAction, row: TRow) => void;

interface ActionMenuProps<
  TAction = string,
  TRow extends object = Record<string, unknown>,
> {
  row?: TRow;
  menuClick?: MenuClickHandler<TAction, TRow>;
  menuFor: MenuForType;
  showItems?: Record<string, boolean>;
}

export const ActionMenu = <
  TAction = string,
  TRow extends object = Record<string, unknown>,
>(
  props: ActionMenuProps<TAction, TRow>,
): React.ReactElement => {
  const menuFor = props.menuFor;
  const row = props.row;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuClick = (
    action: TAction | string,
    row: TRow | Record<string, unknown>,
  ) => {
    if (typeof props.menuClick === "function") {
      props.menuClick(action as TAction, row as TRow);
    }
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreHorizIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menuFor === "user" && (
          <MenuItemUser<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "partner" && (
          <MenuItemPartner<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "tenant" && (
          <MenuItemTenant<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "role" && (
          <MenuItemRole<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "module" && (
          <MenuItemModule<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "subscription" && (
          <MenuItemSubscription<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "feature" && (
          <MenuItemFeature<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "company" && (
          <MenuItemCompany<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "data-limit" && (
          <MenuItemDataLimit<TRow>
            menuFor={menuFor}
            row={row}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "notification" && (
          <MenuItemNotification<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "document" && (
          <MenuItemDocument<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "location" && (
          <MenuItemLocation<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
        {menuFor === "meter" && (
          <MenuItemMeter<TRow>
            menuFor={menuFor}
            row={row}
            showItems={props?.showItems}
            handleClose={handleClose}
            menuClick={menuClick}
          />
        )}
      </Menu>
    </>
  );
};
